import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { setSentryContext } from "@/lib/sentry";
import type { Json } from "@/integrations/supabase/types";
import { toast } from "sonner";

export interface UserCredits {
  subscription_tier: "basic" | "solopreneur" | "reseller";
  monthly_credits_used: number;
  custom_credit_limit: number | null;
  bonus_credits: number;
  is_suspended: boolean;
  last_reset_at: string;
}

export interface CreditUsageLog {
  id: string;
  action: string;
  credits_used: number;
  created_at: string;
  brand_id: string | null;
  brand_name: string | null;
  metadata: Record<string, unknown> | null;
}

export interface UsageLogsPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface CreditsContextType {
  credits: UserCredits | null;
  tierLimit: number;
  usageLogs: CreditUsageLog[];
  usageLogsPagination: UsageLogsPagination;
  loading: boolean;
  isPaidUser: boolean;
  fetchCredits: () => Promise<void>;
  fetchUsageLogs: (page?: number, pageSize?: number) => Promise<void>;
  consumeCredits: (action: string, amount?: number, metadata?: object, brandId?: string | null, isWorkflow?: boolean) => Promise<boolean>;
  consumeWorkflowCredit: (action: string, amount?: number, metadata?: object, brandId?: string | null) => Promise<boolean>;
  refundCredits: (action: string, amount: number, metadata?: object, brandId?: string | null) => Promise<boolean>;
  getEffectiveLimit: () => number;
  getRemainingCredits: () => number;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { isImpersonating, impersonatedUserId } = useImpersonation();
  const effectiveUserId = isImpersonating && impersonatedUserId ? impersonatedUserId : user?.id;
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [tierLimit, setTierLimit] = useState<number>(50);
  const [usageLogs, setUsageLogs] = useState<CreditUsageLog[]>([]);
  const [usageLogsPagination, setUsageLogsPagination] = useState<UsageLogsPagination>({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const lowCreditWarningShown = useRef(false);
  const hasFetchedCreditsOnce = useRef(false);

  const fetchCredits = useCallback(async () => {
    if (!effectiveUserId) {
      setCredits(null);
      setLoading(false);
      return;
    }

    // Only show loading on initial fetch, NOT on refetches.
    // Prevents loading flash on tab-switch when auth token refreshes.
    if (!hasFetchedCreditsOnce.current) {
      setLoading(true);
    }

    const { data: creditsData, error: creditsError } = await supabase
      .from("user_credits")
      .select("*")
      .eq("user_id", effectiveUserId)
      .maybeSingle();

    if (creditsError) {
      console.error("Error fetching user credits:", creditsError);
      setLoading(false);
      return;
    }

    if (creditsData) {
      setSentryContext(creditsData.subscription_tier, null);
      setCredits({
        subscription_tier: creditsData.subscription_tier as "basic" | "solopreneur" | "reseller",
        monthly_credits_used: creditsData.monthly_credits_used,
        custom_credit_limit: creditsData.custom_credit_limit,
        bonus_credits: creditsData.bonus_credits ?? 0,
        is_suspended: creditsData.is_suspended,
        last_reset_at: creditsData.last_reset_at,
      });

      const { data: tierData } = await supabase
        .from("credit_tiers")
        .select("monthly_credit_limit")
        .eq("tier", creditsData.subscription_tier)
        .maybeSingle();

      if (tierData) {
        setTierLimit(tierData.monthly_credit_limit);
      }
    }

    setLoading(false);
    hasFetchedCreditsOnce.current = true;
  }, [effectiveUserId]);

  const fetchUsageLogs = useCallback(async (page = 1, pageSize = 10) => {
    if (!effectiveUserId) return;

    const offset = (page - 1) * pageSize;

    // First get the total count
    const { count, error: countError } = await supabase
      .from("credit_usage_logs")
      .select("*", { count: "exact", head: true })
      .eq("user_id", effectiveUserId);

    if (countError) {
      console.error("Error fetching usage logs count:", countError);
      return;
    }

    const totalCount = count ?? 0;
    const totalPages = Math.ceil(totalCount / pageSize);

    // Fetch logs with brand join
    const { data, error } = await supabase
      .from("credit_usage_logs")
      .select(`
        id, 
        action, 
        credits_used, 
        created_at, 
        brand_id, 
        metadata,
        brand_profiles!credit_usage_logs_brand_id_fkey(business_name)
      `)
      .eq("user_id", effectiveUserId)
      .order("created_at", { ascending: false })
      .range(offset, offset + pageSize - 1);

    if (error) {
      console.error("Error fetching usage logs:", error);
      return;
    }

    // Transform data to include brand_name
    const transformedLogs: CreditUsageLog[] = (data || []).map((log) => {
      const brandProfile = log.brand_profiles as { business_name: string } | null;
      return {
        id: log.id,
        action: log.action,
        credits_used: log.credits_used,
        created_at: log.created_at,
        brand_id: log.brand_id,
        brand_name: brandProfile?.business_name || null,
        metadata: log.metadata as Record<string, unknown> | null,
      };
    });

    setUsageLogs(transformedLogs);
    setUsageLogsPagination({
      page,
      pageSize,
      totalCount,
      totalPages,
    });
  }, [effectiveUserId]);

  const consumeCredits = useCallback(async (action: string, amount: number = 1, metadata: object = {}, brandId?: string | null, isWorkflow: boolean = false) => {
    if (!user || !credits) return false;

    // For workflow mode with paid users, skip optimistic deduction (credits won't change)
    const skipOptimistic = isWorkflow && credits.subscription_tier !== 'basic';

    if (!skipOptimistic) {
      // Optimistically update local state immediately
      setCredits(prev => prev ? {
        ...prev,
        monthly_credits_used: prev.monthly_credits_used + amount
      } : null);
    }

    // Use secure RPC function to consume credits (prevents privilege escalation)
    const { data, error } = await supabase.rpc('consume_credits', {
      p_action: action,
      p_amount: amount,
      p_metadata: metadata as Json,
      p_brand_id: brandId || null,
      p_is_workflow: isWorkflow
    });

    if (error) {
      console.error("Error consuming credits:", error);
      if (!skipOptimistic) {
        setCredits(prev => prev ? {
          ...prev,
          monthly_credits_used: prev.monthly_credits_used - amount
        } : null);
      }
      return false;
    }

    const result = data as { success: boolean; error?: string; credits_used?: number; remaining?: number; workflow_free?: boolean };

    if (!result.success) {
      console.error("Credit consumption failed:", result.error);
      if (result.error === 'Insufficient credits' && !lowCreditWarningShown.current) {
        lowCreditWarningShown.current = true;
        toast.error("Out of credits", {
          description: "Purchase credits or upgrade your plan to continue.",
          action: { label: "Get Credits", onClick: () => window.location.href = "/plan" },
        });
      }
      if (!skipOptimistic) {
        setCredits(prev => prev ? {
          ...prev,
          monthly_credits_used: prev.monthly_credits_used - amount
        } : null);
      }
      return false;
    }

    // Low-credit warning after successful consumption
    if (!skipOptimistic && !lowCreditWarningShown.current) {
      const remaining = result.remaining ?? getRemainingCredits();
      if (remaining === 0) {
        lowCreditWarningShown.current = true;
        toast.error("Out of credits", {
          description: "Purchase credits or upgrade your plan to continue.",
          action: { label: "Get Credits", onClick: () => window.location.href = "/plan" },
        });
      } else if (remaining <= 5) {
        lowCreditWarningShown.current = true;
        toast.warning("Running low on credits", {
          description: `You have ${remaining} credit${remaining !== 1 ? 's' : ''} left.`,
          action: { label: "Get Credits", onClick: () => window.location.href = "/plan" },
        });
      }
    }

    return true;
  }, [user, credits]);

  const isPaidUser = useMemo(() => {
    return credits !== null && credits.subscription_tier !== 'basic';
  }, [credits]);

  const consumeWorkflowCredit = useCallback(async (action: string, amount: number = 1, metadata: object = {}, brandId?: string | null) => {
    if (!user || !credits) return false;

    // For paid users, skip optimistic deduction (workflow is free)
    const skipOptimistic = isPaidUser;

    if (!skipOptimistic) {
      setCredits(prev => prev ? {
        ...prev,
        monthly_credits_used: prev.monthly_credits_used + amount
      } : null);
    }

    const { data, error } = await supabase.rpc('consume_credits', {
      p_action: action,
      p_amount: amount,
      p_metadata: metadata as Json,
      p_brand_id: brandId || null,
      p_is_workflow: true
    });

    if (error) {
      console.error("Error consuming workflow credits:", error);
      if (isPaidUser) return true; // Allow anyway for paid users
      if (!skipOptimistic) {
        setCredits(prev => prev ? {
          ...prev,
          monthly_credits_used: prev.monthly_credits_used - amount
        } : null);
      }
      return false;
    }

    const result = data as { success: boolean; error?: string; workflow_free?: boolean };

    if (!result.success) {
      console.error("Workflow credit consumption failed:", result.error);
      if (isPaidUser) return true; // Allow anyway for paid users
      if (!skipOptimistic) {
        setCredits(prev => prev ? {
          ...prev,
          monthly_credits_used: prev.monthly_credits_used - amount
        } : null);
      }
      toast.error("Insufficient Credits", {
        description: "Upgrade your plan for unlimited workflow access.",
      });
      return false;
    }

    // Refresh credits to sync state
    await fetchCredits();
    return true;
  }, [user, credits, isPaidUser, fetchCredits]);

  const refundCredits = useCallback(async (action: string, amount: number, metadata: object = {}, brandId?: string | null) => {
    if (!user) return false;

    // Optimistically update local state
    setCredits(prev => prev ? {
      ...prev,
      monthly_credits_used: Math.max(0, prev.monthly_credits_used - amount)
    } : null);

    try {
      const { data, error } = await supabase.functions.invoke('refund-credits', {
        body: { action, amount, metadata, brandId: brandId || null }
      });

      if (error || !data?.success) {
        console.error("Refund failed:", error || data?.error);
        // Revert optimistic update
        setCredits(prev => prev ? {
          ...prev,
          monthly_credits_used: prev.monthly_credits_used + amount
        } : null);
        return false;
      }

      return true;
    } catch (err) {
      console.error("Refund error:", err);
      setCredits(prev => prev ? {
        ...prev,
        monthly_credits_used: prev.monthly_credits_used + amount
      } : null);
      return false;
    }
  }, [user]);

  // Returns ONLY the plan limit (for display denominator)
  const getEffectiveLimit = useCallback(() => {
    if (!credits) return tierLimit;
    return credits.custom_credit_limit ?? tierLimit;
  }, [credits, tierLimit]);

  // Returns available = plan_remaining + bonus_credits
  const getRemainingCredits = useCallback(() => {
    if (!credits) return 0;
    const planLimit = getEffectiveLimit();
    const planRemaining = Math.max(0, planLimit - credits.monthly_credits_used);
    return planRemaining + (credits.bonus_credits ?? 0);
  }, [credits, getEffectiveLimit]);

  useEffect(() => {
    // Reset fetch state when switching between impersonated users
    hasFetchedCreditsOnce.current = false;
    fetchCredits();
    fetchUsageLogs();
  }, [effectiveUserId, fetchCredits, fetchUsageLogs]);

  return (
    <CreditsContext.Provider value={{
      credits,
      tierLimit,
      usageLogs,
      usageLogsPagination,
      loading,
      isPaidUser,
      fetchCredits,
      fetchUsageLogs,
      consumeCredits,
      consumeWorkflowCredit,
      refundCredits,
      getEffectiveLimit,
      getRemainingCredits,
    }}>
      {children}
    </CreditsContext.Provider>
  );
}

const CREDITS_FALLBACK: CreditsContextType = {
  credits: null,
  tierLimit: 50,
  usageLogs: [],
  usageLogsPagination: { page: 1, pageSize: 10, totalCount: 0, totalPages: 0 },
  loading: true,
  isPaidUser: false,
  fetchCredits: async () => {},
  fetchUsageLogs: async () => {},
  consumeCredits: async () => false,
  consumeWorkflowCredit: async () => false,
  refundCredits: async () => false,
  getEffectiveLimit: () => 50,
  getRemainingCredits: () => 0,
};

export function useCredits() {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    console.warn("[useCredits] Called outside CreditsProvider — returning safe defaults. This is expected during HMR but should not happen in production.");
    return CREDITS_FALLBACK;
  }
  return context;
}
